import { createConditionPathId } from "./builderIds";

export const CONDITION_TYPES = ["string", "integer", "decimal", "boolean", "date", "date_time", "guid"];
export const CONDITION_DEFAULT_PATH = "default";
export const CONDITION_MAX_RULES = 10;

const INPUT_CAPTURE_CONDITION_TYPES = {
  text: "string",
  email: "string",
  phone: "string",
  integer: "integer",
  decimal: "decimal",
  date: "date",
};

export const conditionTypeForInputCapture = inputType =>
  INPUT_CAPTURE_CONDITION_TYPES[String(inputType || "").trim().toLowerCase()] || null;

export const getInputCaptureConditionFields = nodes => {
  const fields = new Map();

  (Array.isArray(nodes) ? nodes : []).forEach(node => {
    if (String(node?.type || "").trim().toLowerCase() !== "input_capture") return;

    const config = node?.data?.config || {};
    const fieldKey = String(config.fieldKey || "").trim().toLowerCase();
    const inputType = String(config.inputType || "").trim().toLowerCase();
    const comparisonType = conditionTypeForInputCapture(inputType);
    if (!fieldKey || !comparisonType) return;

    const existing = fields.get(fieldKey);
    if (existing) {
      if (!existing.inputTypes.includes(inputType)) existing.inputTypes.push(inputType);
      existing.hasTypeConflict = existing.comparisonTypes.some(type => type !== comparisonType);
      if (!existing.comparisonTypes.includes(comparisonType)) existing.comparisonTypes.push(comparisonType);
      return;
    }

    fields.set(fieldKey, {
      fieldKey,
      inputType,
      inputTypes: [inputType],
      comparisonType,
      comparisonTypes: [comparisonType],
      question: String(config.question || node?.data?.label || "Input Capture").trim(),
      hasTypeConflict: false,
    });
  });

  return Array.from(fields.values()).sort((left, right) =>
    left.fieldKey.localeCompare(right.fieldKey)
  );
};

const universal = ["equals", "not_equals", "is_empty", "is_not_empty"];
const ordered = ["greater_than", "greater_than_or_equal", "less_than", "less_than_or_equal"];
const stringOps = ["contains", "not_contains", "starts_with", "ends_with"];

export const operatorsForConditionType = type => {
  if (type === "string") return [...universal, ...stringOps];
  if (["integer", "decimal", "date", "date_time"].includes(type)) return [...universal, ...ordered];
  return universal;
};

export const createConditionRule = () => ({
  pathId: createConditionPathId(),
  label: "New path",
  operator: "equals",
  comparisonValue: "value",
});

export const createConditionConfig = () => ({
  fieldKey: "custom.value",
  comparisonType: "string",
  rules: [createConditionRule()],
  defaultPathId: CONDITION_DEFAULT_PATH,
});

export function validateConditionConfig(config) {
  if (!config || !Array.isArray(config.rules)) return { isValid: false, message: "Legacy Condition configuration must be replaced." };
  if (!String(config.fieldKey || "").trim()) return { isValid: false, message: "Session Field Key is required." };
  if (!CONDITION_TYPES.includes(config.comparisonType)) return { isValid: false, message: "Comparison Type is unsupported." };
  if (config.rules.length < 1 || config.rules.length > CONDITION_MAX_RULES) return { isValid: false, message: `Condition requires 1-${CONDITION_MAX_RULES} rules.` };
  if (config.defaultPathId !== CONDITION_DEFAULT_PATH) return { isValid: false, message: "Default path must use the default handle." };
  const ids = new Set();
  for (const rule of config.rules) {
    if (!/^rule_[0-9a-f]{32}$/.test(rule.pathId || "") || ids.has(rule.pathId)) return { isValid: false, message: "Condition rule paths must be stable and unique." };
    ids.add(rule.pathId);
    if (!String(rule.label || "").trim() || String(rule.label).trim().length > 80) return { isValid: false, message: "Every Condition rule needs a label of at most 80 characters." };
    if (!operatorsForConditionType(config.comparisonType).includes(rule.operator)) return { isValid: false, message: "A Condition operator is not valid for its comparison type." };
    const emptyOperator = ["is_empty", "is_not_empty"].includes(rule.operator);
    if (emptyOperator && String(rule.comparisonValue || "").trim()) return { isValid: false, message: `${rule.operator} does not accept a comparison value.` };
    if (!emptyOperator && !String(rule.comparisonValue || "").trim()) return { isValid: false, message: `${rule.operator} requires a comparison value.` };
  }
  return { isValid: true, message: null };
}
